import React from 'react';
import { result } from 'lodash';
import styles from './objectMenuStyles.module.css';
import { handlePromoClick } from '../../utils/objectMenu';
import { type PromoLinkType, type PromoLinkValue } from '../../shared-types';
import { usePersistedState } from '@aha-app/aha-shared';
import classNames from 'classnames';

const PERSIST_KEY_PREFIX = 'whiteboard-object-menu-collapsed';

function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .trim()
    .replace(/\s+/g, '-');
}

const ObjectMenuSection = ({
  children,
  title = '',
  titleElement,
  promoLink,
  showNew = false,
  promoTooltipSelector = '',
  onCollapseToggle,
}: {
  children?: React.ReactNode;
  title?: string;
  titleElement?: JSX.Element;
  promoLink?: PromoLinkType;
  showNew?: boolean;
  promoTooltipSelector?: string;
  onCollapseToggle?: (slug: string, collapsed: boolean) => void;
}) => {
  const linkVal = result<PromoLinkValue>({ promoLink }, 'promoLink');
  const slug = title ? slugify(title) : null;
  // Detects if 'custom shapes' is empty
  const hasChildren = !Array.isArray(children?.[0]) || children[0].length > 0;
  const collapsable = hasChildren && !!slug;
  const [collapsed, setCollapsed] = usePersistedState<Boolean>(
    `${PERSIST_KEY_PREFIX}.${slug}`,
    Boolean,
    true
  );

  function toggleCollapse() {
    if (!collapsable) return;

    setCollapsed(collapsed => {
      onCollapseToggle?.(slug, !collapsed);
      return !collapsed;
    });
  }

  const onClickCollapse = (e: React.MouseEvent<HTMLElement>) => {
    // Stop so we don't activeate the promo link
    e.stopPropagation();
    toggleCollapse();
  };

  return (
    <div className={classNames(collapsed && styles.objectMenuSectionCollapsed)}>
      {title.length > 0 && (
        <div
          className={styles.objectMenuSectionHeading}
          style={{ cursor: linkVal ? 'pointer' : 'default' }}
          onClick={() => {
            if (linkVal) {
              handlePromoClick(linkVal);
            } else {
              toggleCollapse();
            }
          }}
          data-promo-target={promoTooltipSelector}
        >
          <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
            {titleElement ?? title}
            {linkVal && (
              <aha-promo-try-pill
                font-sizing='large'
                pill-size='small'
                upgrade-location={linkVal.split('?upgrade_location=').pop()}
                style={{ marginLeft: 'var(--spacer-2)', cursor: 'pointer' }}
              />
            )}
            {!linkVal && showNew && <span className='new-icon ml-1' />}
          </div>
          {collapsable && (
            <div className={styles.arrow} onClick={onClickCollapse}>
              <aha-icon icon='fa-solid fa-angle-down' />
            </div>
          )}
        </div>
      )}
      {hasChildren && (
        <div
          data-testid={`object-menu-content-${title
            .toLowerCase()
            .replace(' ', '-')}`}
          className={styles.objectMenuContent}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default ObjectMenuSection;
